import { useState, type ReactNode } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { AlertTriangle, ChevronRight, Loader2 } from "lucide-react";
import type {
  AccountantTenantDetailHealth,
  AccountantTenantDetailPendingAck,
  AccountantTenantDetailRejection,
} from "@fiscalo/shared-types";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  accountantAcceptAllPendingRecibidas,
  accountantDismissInvoiceRejection,
} from "@/services/dashboardService";

/** Same key + fallback the CAE cards use for a CFE type label. */
function cfeTypeLabel(cfeType: string, t: (key: string) => string): string {
  const key = `invoices.cfeTypes.${cfeType}`;
  const translated = t(key);
  return translated !== key ? translated : `CFE ${cfeType}`;
}

/** One yellow actionable alert: icon + title/detail on the left, actions on the right. */
function AlertBox({
  title,
  detail,
  actions,
}: {
  title: string;
  detail?: string;
  actions?: ReactNode;
}): JSX.Element {
  return (
    <Card className="border-yellow-300 bg-yellow-50 dark:border-yellow-700/40 dark:bg-yellow-950/20">
      <CardContent className="flex flex-wrap items-start gap-3 py-4">
        <AlertTriangle
          className="mt-0.5 h-4 w-4 shrink-0 text-yellow-600"
          aria-hidden
        />
        <div className="min-w-0 flex-1 text-sm">
          <div className="font-medium text-foreground">{title}</div>
          {detail ? (
            <div className="text-xs text-muted-foreground">{detail}</div>
          ) : null}
        </div>
        {actions ? (
          <div className="flex shrink-0 flex-wrap items-center gap-2">
            {actions}
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

/**
 * Actionable health alerts for the accountant's tenant detail page. Replaces
 * the old Salud-fiscal breakdown list + standalone pending-ACK box: each
 * active issue is its own yellow box with a way to resolve it.
 *
 * - Pending recibidas → accept all in place (server enumerates the same set
 *   the count reflects) or jump to the ACK detail to accept/reject per
 *   document (rejection needs motivos, so it's detail-only).
 * - Open DGI rejections → one row each, showing the motivo, dismissed in
 *   place. Previously reachable only through "Acceder como cliente", because
 *   both existing dismiss routes resolve the tenant from the session.
 * - Overdue / due-soon obligations → scroll to "Próximos vencimientos" (which
 *   carries the per-row "Marcar pagada").
 * - Missing BPS number → jump to the Perfil fiscal tab.
 * - Missing 206/019 declaration → jump to the Perfil fiscal tab.
 *
 * The declaration alert does NOT come from `deductions`. The cartera score
 * (auth.service.ts) deducts for a pending declaration, but this page's score
 * comes from calculateFiscalHealthScore, which only knows DGI obligations —
 * so the row read "Falta declaración 206/019" in the cartera while the client
 * page said 100/100, "sin deducciones activas". The flag is passed in
 * separately rather than faked into a deduction, so the score stays honest
 * about what it measures and the alert still appears.
 */
export function AccountantHealthAlerts({
  tenantId,
  deductions,
  pendingAck,
  fiscalBenefitPending,
  rejections,
  onGoToObligations,
  onGoToProfile,
}: {
  tenantId: string;
  deductions: AccountantTenantDetailHealth["deductions"];
  pendingAck: AccountantTenantDetailPendingAck;
  /** No standing 206/019 declaration, or one from an earlier ejercicio. */
  fiscalBenefitPending: boolean;
  /** Open (undismissed) DGI rejections — the cartera pill's rows. */
  rejections: AccountantTenantDetailRejection[];
  onGoToObligations: () => void;
  onGoToProfile: () => void;
}): JSX.Element | null {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [confirmOpen, setConfirmOpen] = useState(false);

  const dismissRejection = useMutation({
    mutationFn: (invoiceId: string) =>
      accountantDismissInvoiceRejection(tenantId, invoiceId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["accountant", "tenant-detail", tenantId],
      });
      toast.success(
        t(
          "accountantClientDetail.alerts.rejections.dismissed",
          "Rechazo marcado como resuelto",
        ),
      );
    },
    onError: () => {
      toast.error(
        t(
          "accountantClientDetail.alerts.rejections.dismissError",
          "No pudimos marcar el rechazo",
        ),
      );
    },
  });

  const acceptAll = useMutation({
    mutationFn: () => accountantAcceptAllPendingRecibidas(tenantId),
    onSuccess: async (result) => {
      setConfirmOpen(false);
      await queryClient.invalidateQueries({
        queryKey: ["accountant", "tenant-detail", tenantId],
      });
      toast.success(
        t(
          "accountantClientDetail.alerts.ack.accepted",
          "{{count}} comprobante(s) aceptado(s)",
          { count: result.acceptedCount },
        ),
      );
      if (result.failedCount > 0) {
        toast.error(
          t(
            "accountantClientDetail.alerts.ack.someFailed",
            "{{count}} no se pudieron aceptar",
            { count: result.failedCount },
          ),
        );
      }
    },
    onError: () => {
      toast.error(
        t(
          "accountantClientDetail.alerts.ack.acceptError",
          "No pudimos aceptar los comprobantes",
        ),
      );
    },
  });

  const obligationDeductions = deductions.filter(
    (d) => d.code === "overdue" || d.code === "due_in_3",
  );
  const hasMissingBps = deductions.some((d) => d.code === "missing_bps");

  if (
    pendingAck.count === 0 &&
    obligationDeductions.length === 0 &&
    !hasMissingBps &&
    !fiscalBenefitPending &&
    rejections.length === 0
  ) {
    return null;
  }

  return (
    <div className="space-y-3">
      {fiscalBenefitPending ? (
        <AlertBox
          title={t(
            "accountantClientDetail.alerts.fiscalBenefit.title",
            "Falta la declaración del beneficio fiscal (Decreto 206/019)",
          )}
          detail={t(
            "accountantClientDetail.alerts.fiscalBenefit.detail",
            "Sin declaración vigente no se puede aplicar ni informar el crédito de 80 UI, y no se puede cambiar de plan.",
          )}
          actions={
            <Button size="sm" variant="outline" onClick={onGoToProfile}>
              {t(
                "accountantClientDetail.alerts.fiscalBenefit.goto",
                "Completar declaración",
              )}
              <ChevronRight className="ml-1 h-4 w-4" aria-hidden />
            </Button>
          }
        />
      ) : null}

      {/* One box per open rechazo, NOT one box with a count and a "Descartar
          todos". The tenant's own dashboard has bulk, and copying it here
          would be the obvious move — but an accountant is reviewing someone
          else's books days later, where most rows are already-fixed noise and
          the occasional one is genuinely unsent. Bulk is precisely the control
          that buries that one while feeling like progress. The motivo is shown
          for the same reason: without it the accountant is asked to make the
          judgement that sent them to the invoice page to begin with. */}
      {rejections.map((r) => {
        const motivo = r.motivos
          .map((m) => [m.code, m.detail || m.description].filter(Boolean).join(" — "))
          .join(" · ");
        const dismissing =
          dismissRejection.isPending && dismissRejection.variables === r.invoiceId;
        return (
          <AlertBox
            key={r.invoiceId}
            title={t(
              "accountantClientDetail.alerts.rejections.title",
              "{{cfe}} {{series}}{{number}} rechazado por DGI ({{date}})",
              {
                cfe: cfeTypeLabel(r.cfeType, t),
                series: r.series,
                number: r.number ?? "",
                date: r.issueDate,
              },
            )}
            /* Not "DGI todavía no publicó el motivo" — that promises a motivo
               is coming, which is false for the older rows whose rejection was
               recorded without one (GAMA's Jan–Feb CFEs) and which the motivo
               sweep no longer looks at. Say only what we know. */
            detail={
              motivo ||
              t(
                "accountantClientDetail.alerts.rejections.noMotivo",
                "Sin motivo registrado — revisá el comprobante.",
              )
            }
            actions={
              <Button
                size="sm"
                variant="outline"
                onClick={() => dismissRejection.mutate(r.invoiceId)}
                disabled={dismissRejection.isPending}
              >
                {dismissing ? (
                  <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                ) : null}
                {t(
                  "accountantClientDetail.alerts.rejections.dismiss",
                  "Marcar resuelto",
                )}
              </Button>
            }
          />
        );
      })}

      {pendingAck.count > 0 ? (
        <AlertBox
          title={t(
            "accountantClientDetail.pendingAck.title",
            "{{count}} comprobante(s) recibido(s) sin aceptar",
            { count: pendingAck.count },
          )}
          detail={
            pendingAck.overdueCount > 0
              ? t(
                  "accountantClientDetail.pendingAck.overdue",
                  "{{count}} ya pasaron los 20 días reglamentarios.",
                  { count: pendingAck.overdueCount },
                )
              : t(
                  "accountantClientDetail.pendingAck.window",
                  "Tienen 20 días corridos para responder desde su recepción.",
                )
          }
          actions={
            <>
              <Button
                size="sm"
                onClick={() => setConfirmOpen(true)}
                disabled={acceptAll.isPending}
              >
                {t("accountantClientDetail.alerts.ack.acceptAll", "Aceptar todas")}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() =>
                  navigate(
                    `/accountant/inbox/acks?tenant=${encodeURIComponent(tenantId)}`,
                  )
                }
              >
                {t("accountantClientDetail.alerts.ack.review", "Ver detalle")}
                <ChevronRight className="ml-1 h-4 w-4" aria-hidden />
              </Button>
            </>
          }
        />
      ) : null}

      {obligationDeductions.map((d) => (
        <AlertBox
          key={d.code}
          title={d.label}
          detail={t(
            "accountantClientDetail.alerts.obligations.detail",
            "Registrá el pago desde Próximos vencimientos.",
          )}
          actions={
            <Button size="sm" variant="outline" onClick={onGoToObligations}>
              {t(
                "accountantClientDetail.alerts.obligations.goto",
                "Ver vencimientos",
              )}
              <ChevronRight className="ml-1 h-4 w-4" aria-hidden />
            </Button>
          }
        />
      ))}

      {hasMissingBps ? (
        <AlertBox
          title={t(
            "accountantClientDetail.alerts.missingBps.title",
            "Falta el número de empresa BPS",
          )}
          detail={t(
            "accountantClientDetail.alerts.missingBps.detail",
            "Completá el perfil fiscal del cliente.",
          )}
          actions={
            <Button size="sm" variant="outline" onClick={onGoToProfile}>
              {t(
                "accountantClientDetail.alerts.missingBps.goto",
                "Completar perfil",
              )}
              <ChevronRight className="ml-1 h-4 w-4" aria-hidden />
            </Button>
          }
        />
      ) : null}

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {t(
                "accountantClientDetail.alerts.ack.confirmTitle",
                "Aceptar todos los comprobantes",
              )}
            </DialogTitle>
            <DialogDescription>
              {t(
                "accountantClientDetail.alerts.ack.confirmBody",
                "Vas a aceptar {{count}} comprobante(s) recibido(s). Se enviará el acuse (ACK) a cada emisor. El rechazo se hace por documento desde el detalle.",
                { count: pendingAck.count },
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setConfirmOpen(false)}
              disabled={acceptAll.isPending}
            >
              {t("common.cancel", "Cancelar")}
            </Button>
            <Button
              onClick={() => acceptAll.mutate()}
              disabled={acceptAll.isPending}
            >
              {acceptAll.isPending ? (
                <Loader2 className="mr-1 h-4 w-4 animate-spin" />
              ) : null}
              {t("accountantClientDetail.alerts.ack.acceptAll", "Aceptar todas")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
